import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaDownload,
  FaExclamationTriangle,
  FaEye,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaWallet,
} from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { downloadInvoicePdf, loadImageAsDataUrl } from "../utils/invoicePdf";
import { getPortalSession } from "./portalSession";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import PortalAlert from "./PortalAlert";
import PortalResultsHead from "./PortalResultsHead";
import PortalFiltersCollapsible, { usePortalCompactFilters } from "./PortalFiltersCollapsible";
import PortalSelect from "./PortalSelect";
import PortalPayDialog from "./PortalPayDialog";
import "../assets/css/DigixLeads.css";

const ROWS_PER_PAGE_KEY = "portalFinanceRowsPerPage";

const VIEW_OUTSTANDING = "outstanding";
const VIEW_HISTORY = "history";
const VIEW_ALL = "all";
const VIEW_PAYMENTS = "payments";

const INVOICE_COLUMNS = [
  { key: "invoiceNo", label: "Invoice" },
  { key: "invoiceDate", label: "Date" },
  { key: "dueDate", label: "Due" },
  { key: "total", label: "Total" },
  { key: "paid", label: "Paid" },
  { key: "balance", label: "Balance" },
  { key: "status", label: "Status" },
];

const PAYMENT_COLUMNS = [
  { key: "paymentDate", label: "Date" },
  { key: "invoiceNo", label: "Invoice" },
  { key: "amount", label: "Amount" },
  { key: "mode", label: "Mode" },
  { key: "ref", label: "Reference" },
  { key: "status", label: "Status" },
];

const INVOICE_SORTERS = {
  invoiceNo: (r) => (r.Invoice_No || "").toLowerCase(),
  invoiceDate: (r) => r.Invoice_Date || "",
  dueDate: (r) => r.Due_Date || "",
  total: (r) => Number(r.Total_Amount || 0),
  paid: (r) => Number(r.Paid_Amount || 0),
  balance: (r) => Number(r.Balance_Amount || 0),
  status: (r) => (r.Status || "").toLowerCase(),
};

const PAYMENT_SORTERS = {
  paymentDate: (r) => r.Payment_Date || "",
  invoiceNo: (r) => (r.Invoice_No || "").toLowerCase(),
  amount: (r) => Number(r.Amount || 0),
  mode: (r) => (r.Payment_Mode_Name || "").toLowerCase(),
  ref: (r) => (r.Transaction_Ref || "").toLowerCase(),
  status: (r) => (r.Payment_Status || "").toLowerCase(),
};

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseLocalDate(value) {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DUE_SOON_DAYS = 7;

/** Countdown label + tone for a due date: overdue, today, soon (within a week) or upcoming. */
export function dueStatus(value) {
  const due = parseLocalDate(value);
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / 86400000);
  if (days < 0) {
    const n = -days;
    return { days, tone: "overdue", label: `Overdue by ${n} ${n === 1 ? "day" : "days"}` };
  }
  if (days === 0) return { days, tone: "today", label: "Due today" };
  if (days === 1) return { days, tone: "soon", label: "Due tomorrow" };
  if (days <= DUE_SOON_DAYS) return { days, tone: "soon", label: `Due in ${days} days` };
  return { days, tone: "info", label: `In ${days} days` };
}

function DueDate({ value, open }) {
  const status = open ? dueStatus(value) : null;
  if (!status) return formatDate(value);
  return (
    <span className={`cportal-due-date cportal-due-date--${status.tone}`}>
      <span className="cportal-due-date__value">{formatDate(value)}</span>
      <span className="cportal-due-date__hint">{status.label}</span>
    </span>
  );
}

export function formatMoney(value, currency) {
  const n = Number(value || 0);
  const text = n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${text}` : text;
}

export function invoiceStatusTone(invoice) {
  if (invoice?.Is_Overdue) return "danger";
  const status = String(invoice?.Status || "").toLowerCase();
  if (status === "paid") return "success";
  if (status === "cancelled") return "neutral";
  if (status === "partially paid") return "warning";
  return "info";
}

export async function downloadPortalInvoicePdf(invoice, session) {
  const issuer = invoice.Issuer_Company
    ? {
        ...invoice.Issuer_Company,
        logoDataUrl: await loadImageAsDataUrl(invoice.Issuer_Company.Logo_Url),
      }
    : null;
  downloadInvoicePdf({
    invoice: { ...invoice, currency: invoice.Currency_Code || "" },
    billedTo: {
      companyName: session?.Company_Name || "",
      email: session?.Company_Email || session?.Email || "",
    },
    lineItems: invoice.items || [],
    payments: invoice.payments || [],
    issuer,
  });
}

function badgeClass(tone) {
  return tone === "neutral" ? "cportal-badge" : `cportal-badge cportal-badge--${tone}`;
}

function statusLabel(invoice) {
  return invoice?.Is_Overdue ? "Overdue" : invoice?.Status || "—";
}

function paymentTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "pending") return "warning";
  if (s === "failed" || s === "cancelled") return "danger";
  return "neutral";
}

function TenantFinance() {
  const navigate = useNavigate();
  const { filtersOpen, setFiltersOpen, isCompactViewport } = usePortalCompactFilters();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState(VIEW_OUTSTANDING);
  const [search, setSearch] = useState("");
  const [resultKeyword, setResultKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortKey, setSortKey] = useState("invoiceDate");
  const [sortDir, setSortDir] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);
  const [pay, setPay] = useState({ open: false, arId: null });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(ROWS_PER_PAGE_KEY), 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(saved)) return saved;
    } catch {
      /* ignore */
    }
    return 10;
  });

  const showingPayments = view === VIEW_PAYMENTS;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [summaryRes, invoicesRes, paymentsRes] = await Promise.all([
          axiosInstance.get("/client/finance/summary"),
          axiosInstance.get("/client/finance/invoices"),
          axiosInstance.get("/client/finance/payments"),
        ]);
        if (!active) return;
        setSummary(summaryRes.data?.data || null);
        setInvoices(Array.isArray(invoicesRes.data?.data) ? invoicesRes.data.data : []);
        setPayments(Array.isArray(paymentsRes.data?.data) ? paymentsRes.data.data : []);
      } catch (err) {
        if (active) setError(err.response?.data?.message || "Could not load your billing details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const outstanding = useMemo(() => invoices.filter((i) => i.Is_Open), [invoices]);
  const history = useMemo(() => invoices.filter((i) => !i.Is_Open), [invoices]);

  const statuses = useMemo(
    () => Array.from(new Set(invoices.map((i) => i.Status).filter(Boolean))).sort(),
    [invoices]
  );

  const baseRows = useMemo(() => {
    if (view === VIEW_OUTSTANDING) return outstanding;
    if (view === VIEW_HISTORY) return history;
    if (view === VIEW_PAYMENTS) return payments;
    return invoices;
  }, [view, outstanding, history, payments, invoices]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return baseRows.filter((row) => {
      if (!showingPayments && statusFilter && String(row.Status || "") !== statusFilter) return false;
      if (!q) return true;
      const haystack = showingPayments
        ? [row.Invoice_No, row.Transaction_Ref, row.Payment_Mode_Name, row.Payment_Status]
        : [row.Invoice_No, row.Status, row.Project_Name, row.Plan_Name];
      return haystack.filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [baseRows, search, statusFilter, showingPayments]);

  const resultFiltered = useMemo(() => {
    const q = resultKeyword.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((row) =>
      Object.values(row)
        .filter((v) => typeof v === "string" || typeof v === "number")
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [filtered, resultKeyword]);

  const sorted = useMemo(() => {
    const sorters = showingPayments ? PAYMENT_SORTERS : INVOICE_SORTERS;
    const accessor = sorters[sortKey] || Object.values(sorters)[0];
    const next = [...resultFiltered];
    next.sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return next;
  }, [resultFiltered, sortKey, sortDir, showingPayments]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage) || 1);
  const page = Math.min(currentPage, totalPages);
  const pageRows = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const skipResetRef = useRef(true);
  useEffect(() => {
    if (skipResetRef.current) {
      skipResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [view, search, resultKeyword, statusFilter]);

  const changeView = (next) => {
    setView(next);
    setStatusFilter("");
    setSortKey(next === VIEW_PAYMENTS ? "paymentDate" : "invoiceDate");
    setSortDir("desc");
  };

  const toggleSort = (key) => {
    setCurrentPage(1);
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const openInvoice = (arId) => navigate(`/portal/finance/invoices/${arId}`);
  const openPay = (arId = null) => setPay({ open: true, arId });
  const closePay = () => setPay({ open: false, arId: null });
  const hasOutstanding = Number(summary?.Outstanding_Amount || 0) > 0;
  const nextDueDate = parseLocalDate(summary?.Next_Due_Date);
  const nextDue = summary?.Next_Due_Date ? dueStatus(summary.Next_Due_Date) : null;
  const bannerTone = summary?.Overdue_Invoices ? "overdue" : nextDue?.tone || "info";

  const handleDownload = async (arId) => {
    if (!arId || downloadingId) return;
    setDownloadingId(arId);
    try {
      const res = await axiosInstance.get(`/client/finance/invoices/${arId}`);
      const invoice = res.data?.data;
      if (!invoice) throw new Error("missing invoice");
      await downloadPortalInvoicePdf(invoice, getPortalSession());
    } catch (_) {
      setError("Could not download the invoice PDF.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(ROWS_PER_PAGE_KEY, String(next));
  };

  const currency = summary?.Currency_Code || "";
  const tiles = [
    {
      key: "outstanding",
      label: "Outstanding",
      value: formatMoney(summary?.Outstanding_Amount, currency),
      icon: FaFileInvoiceDollar,
      tone: "outstanding",
      view: VIEW_OUTSTANDING,
    },
    {
      key: "overdue",
      label: summary?.Overdue_Invoices ? `Overdue (${summary.Overdue_Invoices})` : "Overdue",
      value: formatMoney(summary?.Overdue_Amount, currency),
      icon: FaExclamationTriangle,
      tone: "overdue",
      view: VIEW_OUTSTANDING,
    },
    {
      key: "paid",
      label: "Total Paid",
      value: formatMoney(summary?.Total_Paid, currency),
      icon: FaCheckCircle,
      tone: "paid",
      view: VIEW_HISTORY,
    },
    {
      key: "payments",
      label: "Payments Recorded",
      value: payments.length,
      icon: FaWallet,
      tone: "payments",
      view: VIEW_PAYMENTS,
    },
  ];

  const tabs = [
    { key: VIEW_OUTSTANDING, label: "Outstanding", count: outstanding.length },
    { key: VIEW_HISTORY, label: "History", count: history.length },
    { key: VIEW_ALL, label: "All Invoices", count: invoices.length },
    { key: VIEW_PAYMENTS, label: "Payments", count: payments.length },
  ];

  const emptyLabel = showingPayments
    ? "No payments recorded yet."
    : view === VIEW_OUTSTANDING
      ? "Nothing outstanding. You are all paid up."
      : view === VIEW_HISTORY
        ? "No settled invoices yet."
        : "No invoices yet.";

  const columns = showingPayments ? PAYMENT_COLUMNS : INVOICE_COLUMNS;

  return (
    <div className="cportal-page cportal-finance">
      <div className="cportal-page-head">
        <h1>Finance</h1>
      </div>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />

      <div className="cportal-stats cportal-finance-stats">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <button
              key={tile.key}
              type="button"
              className={`cportal-stat-card cportal-finance-stat cportal-finance-stat--${tile.tone}`}
              onClick={() => changeView(tile.view)}
            >
              <span className="cportal-stat-icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="cportal-stat-value">{loading ? "—" : tile.value}</span>
              <span className="cportal-stat-label">{tile.label}</span>
            </button>
          );
        })}
      </div>

      {summary?.Next_Due_Date || hasOutstanding ? (
        <div className={`cportal-due-banner cportal-due-banner--${bannerTone}`}>
          {nextDueDate ? (
            <div className="cportal-due-banner__cal" aria-hidden="true">
              <span className="cportal-due-banner__month">
                {nextDueDate.toLocaleDateString("en-GB", { month: "short" })}
              </span>
              <span className="cportal-due-banner__day">{nextDueDate.getDate()}</span>
            </div>
          ) : (
            <div className="cportal-due-banner__cal cportal-due-banner__cal--icon" aria-hidden="true">
              <FaCalendarAlt />
            </div>
          )}
          <div className="cportal-due-banner__text">
            <span className="cportal-due-banner__title">
              {summary?.Overdue_Invoices
                ? `${summary.Overdue_Invoices} ${summary.Overdue_Invoices === 1 ? "invoice is" : "invoices are"} overdue`
                : nextDueDate
                  ? "Next payment due"
                  : "Payment pending"}
            </span>
            <span className="cportal-due-banner__sub">
              {nextDueDate ? <strong>{formatDate(summary.Next_Due_Date)}</strong> : null}
              {nextDue ? <span className="cportal-due-banner__chip">{nextDue.label}</span> : null}
              {summary?.Overdue_Invoices ? (
                <span>Overdue amount {formatMoney(summary.Overdue_Amount, currency)}</span>
              ) : null}
            </span>
          </div>
          {hasOutstanding ? (
            <button type="button" className="cportal-btn cportal-btn-primary cportal-finance-pay-now" onClick={() => openPay(null)}>
              <FaMoneyBillWave aria-hidden="true" /> Pay {formatMoney(summary.Outstanding_Amount, currency)}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="cportal-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`cportal-tab${view === tab.key ? " active" : ""}`}
            onClick={() => changeView(tab.key)}
          >
            {tab.label}
            <span className="cportal-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      <label className="cportal-finance-tabs-select">
        <span className="cportal-finance-tabs-select__label">View</span>
        <select
          value={view}
          onChange={(e) => changeView(e.target.value)}
          aria-label="Finance view"
        >
          {tabs.map((tab) => (
            <option key={tab.key} value={tab.key}>
              {tab.label} ({tab.count})
            </option>
          ))}
        </select>
      </label>

      <PortalFiltersCollapsible
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setCurrentPage(1);
        }}
        searchPlaceholder={
          showingPayments ? "Search invoice, reference, mode" : "Search invoice, status, project"
        }
        searchAriaLabel={showingPayments ? "Search payments" : "Search invoices"}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        isCompactViewport={isCompactViewport}
        hasActiveFilters={Boolean(search || statusFilter)}
        onClear={() => {
          setSearch("");
          setStatusFilter("");
          setCurrentPage(1);
        }}
      >
        {!showingPayments && (
          <div className="cportal-filter-row">
            <PortalSelect
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </PortalSelect>
          </div>
        )}
      </PortalFiltersCollapsible>

      <div className="cportal-card">
        <PortalResultsHead
          heading={showingPayments ? "Payments" : "Invoices"}
          count={sorted.length}
          keyword={resultKeyword}
          onKeywordChange={setResultKeyword}
          ariaLabel={showingPayments ? "Keyword search payments" : "Keyword search invoices"}
        />

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">
            {baseRows.length === 0 ? emptyLabel : "Nothing matches your filters."}
          </p>
        ) : (
          <>
            <div className="cportal-table-wrap co-desktop-table">
              <table className="cportal-table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} className="cportal-th-sortable">
                        <SupportSortableHeader
                          columnId={col.key}
                          label={col.label}
                          sortColumn={sortKey}
                          sortDirection={sortDir}
                          onSort={toggleSort}
                        />
                      </th>
                    ))}
                    <th className="cportal-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {showingPayments
                    ? pageRows.map((p) => (
                        <tr key={p.Payment_Id}>
                          <td>{formatDate(p.Payment_Date)}</td>
                          <td>
                            <button
                              type="button"
                              className="cportal-table-link"
                              onClick={() => navigate(`/portal/finance/invoices/${p.AR_Id}`)}
                            >
                              {p.Invoice_No || "—"}
                            </button>
                          </td>
                          <td>{formatMoney(p.Amount, p.Currency_Code || currency)}</td>
                          <td>{p.Payment_Mode_Name || "—"}</td>
                          <td>{p.Transaction_Ref || "—"}</td>
                          <td>
                            <span className={badgeClass(paymentTone(p.Payment_Status))}>
                              {p.Payment_Status || "—"}
                            </span>
                          </td>
                          <td className="cportal-td-actions">
                            <div className="cportal-re-actions">
                              <button
                                type="button"
                                className="re-lead-action re-lead-action--view"
                                onClick={() => openInvoice(p.AR_Id)}
                                title="View Invoice"
                                aria-label="View Invoice"
                              >
                                <FaEye size={16} />
                              </button>
                              <button
                                type="button"
                                className="re-lead-action re-lead-action--share"
                                onClick={() => handleDownload(p.AR_Id)}
                                disabled={Boolean(downloadingId)}
                                title="Download Invoice PDF"
                                aria-label="Download Invoice PDF"
                              >
                                <FaDownload size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    : pageRows.map((inv) => (
                        <tr key={inv.AR_Id}>
                          <td>
                            <button
                              type="button"
                              className="cportal-table-link"
                              onClick={() => navigate(`/portal/finance/invoices/${inv.AR_Id}`)}
                            >
                              {inv.Invoice_No || "—"}
                            </button>
                          </td>
                          <td>{formatDate(inv.Invoice_Date)}</td>
                          <td>
                            <DueDate value={inv.Due_Date} open={inv.Is_Open} />
                          </td>
                          <td>{formatMoney(inv.Total_Amount, inv.Currency_Code || currency)}</td>
                          <td>{formatMoney(inv.Paid_Amount, inv.Currency_Code || currency)}</td>
                          <td>{formatMoney(inv.Balance_Amount, inv.Currency_Code || currency)}</td>
                          <td>
                            <span className={badgeClass(invoiceStatusTone(inv))}>
                              {statusLabel(inv)}
                            </span>
                          </td>
                          <td className="cportal-td-actions">
                            <div className="cportal-re-actions">
                              {inv.Is_Open ? (
                                <button
                                  type="button"
                                  className="re-lead-action re-lead-action--pay"
                                  onClick={() => openPay(inv.AR_Id)}
                                  title="Pay this invoice"
                                  aria-label={`Pay invoice ${inv.Invoice_No || ""}`}
                                >
                                  <FaMoneyBillWave size={16} />
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="re-lead-action re-lead-action--view"
                                onClick={() => openInvoice(inv.AR_Id)}
                                title="View Invoice"
                                aria-label="View Invoice"
                              >
                                <FaEye size={16} />
                              </button>
                              <button
                                type="button"
                                className="re-lead-action re-lead-action--share"
                                onClick={() => handleDownload(inv.AR_Id)}
                                disabled={Boolean(downloadingId)}
                                title="Download Invoice PDF"
                                aria-label="Download Invoice PDF"
                              >
                                <FaDownload size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>

            <ResultsMobileList emptyLabel={emptyLabel} className="cportal-finance-mobile">
              {showingPayments
                ? pageRows.map((p) => (
                    <ResultsMobileCard
                      key={p.Payment_Id}
                      title={formatMoney(p.Amount, p.Currency_Code || currency)}
                      subtitle={p.Invoice_No || null}
                      meta={[
                        {
                          label: p.Payment_Status || "—",
                          tone: paymentTone(p.Payment_Status),
                          status: true,
                        },
                      ]}
                      fields={[
                        { label: "Paid on", value: formatDate(p.Payment_Date) },
                        { label: "Mode", value: p.Payment_Mode_Name },
                        { label: "Reference", value: p.Transaction_Ref },
                      ]}
                      onOpen={() => openInvoice(p.AR_Id)}
                      actions={
                        <div className="co-mcard__action-grid">
                          <button
                            type="button"
                            className="co-maction"
                            title="View Invoice"
                            aria-label="View Invoice"
                            onClick={() => openInvoice(p.AR_Id)}
                          >
                            <FaEye size={14} />
                            <span>View</span>
                          </button>
                          <button
                            type="button"
                            className="co-maction"
                            title="Download Invoice PDF"
                            aria-label="Download Invoice PDF"
                            disabled={Boolean(downloadingId)}
                            onClick={() => handleDownload(p.AR_Id)}
                          >
                            <FaDownload size={14} />
                            <span>{downloadingId === p.AR_Id ? "Preparing…" : "PDF"}</span>
                          </button>
                        </div>
                      }
                    />
                  ))
                : pageRows.map((inv) => (
                    <ResultsMobileCard
                      key={inv.AR_Id}
                      title={inv.Invoice_No || "—"}
                      subtitle={inv.Plan_Name || inv.Project_Name || null}
                      meta={[
                        { label: statusLabel(inv), tone: invoiceStatusTone(inv), status: true },
                      ]}
                      fields={[
                        { label: "Invoice date", value: formatDate(inv.Invoice_Date) },
                        { label: "Due date", value: <DueDate value={inv.Due_Date} open={inv.Is_Open} /> },
                        {
                          label: "Total",
                          value: formatMoney(inv.Total_Amount, inv.Currency_Code || currency),
                        },
                        {
                          label: "Paid",
                          value: formatMoney(inv.Paid_Amount, inv.Currency_Code || currency),
                        },
                        {
                          label: "Balance",
                          value: formatMoney(inv.Balance_Amount, inv.Currency_Code || currency),
                        },
                      ]}
                      onOpen={() => openInvoice(inv.AR_Id)}
                      actions={
                        <div className="co-mcard__action-grid">
                          {inv.Is_Open ? (
                            <button
                              type="button"
                              className="co-maction co-maction--pay"
                              title="Pay this invoice"
                              aria-label={`Pay invoice ${inv.Invoice_No || ""}`}
                              onClick={() => openPay(inv.AR_Id)}
                            >
                              <FaMoneyBillWave size={14} />
                              <span>Pay</span>
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="co-maction"
                            title="View Invoice"
                            aria-label="View Invoice"
                            onClick={() => openInvoice(inv.AR_Id)}
                          >
                            <FaEye size={14} />
                            <span>View</span>
                          </button>
                          <button
                            type="button"
                            className="co-maction"
                            title="Download Invoice PDF"
                            aria-label="Download Invoice PDF"
                            disabled={Boolean(downloadingId)}
                            onClick={() => handleDownload(inv.AR_Id)}
                          >
                            <FaDownload size={14} />
                            <span>{downloadingId === inv.AR_Id ? "Preparing…" : "PDF"}</span>
                          </button>
                        </div>
                      }
                    />
                  ))}
            </ResultsMobileList>
          </>
        )}

        {!loading && sorted.length > 0 && (
          <TablePagination
            currentPage={page}
            totalItems={sorted.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel={showingPayments ? "payments" : "invoices"}
          />
        )}
      </div>

      <PortalPayDialog open={pay.open} arId={pay.arId} onClose={closePay} />
    </div>
  );
}

export default TenantFinance;
